import { useCallback, useSyncExternalStore } from 'react'

/**
 * The whole router. The docs are a flat set of pages keyed by pathname, so a
 * dependency would cost more than it saves — nginx already falls back to
 * index.html, which is the only server-side cooperation history routing needs.
 */

function subscribe(onChange: () => void) {
  window.addEventListener('popstate', onChange)
  window.addEventListener('legion-docs:navigate', onChange)
  return () => {
    window.removeEventListener('popstate', onChange)
    window.removeEventListener('legion-docs:navigate', onChange)
  }
}

function getPath() {
  return window.location.pathname
}

/** Current pathname, re-rendering on both real and pushState navigation. */
export function usePath(): string {
  return useSyncExternalStore(subscribe, getPath, () => '/')
}

/** Navigate without a reload. Same path is a no-op so links do not stack history. */
export function navigate(to: string) {
  if (window.location.pathname === to) return
  window.history.pushState(null, '', to)
  window.dispatchEvent(new Event('legion-docs:navigate'))
}

/**
 * Props for an anchor that navigates client-side but stays a real link:
 * middle-click, ctrl-click and "open in new tab" must keep working, so only
 * unmodified left clicks are intercepted.
 */
export function useLinkProps(to: string) {
  const onClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return
      }
      e.preventDefault()
      navigate(to)
    },
    [to]
  )
  return { href: to, onClick }
}
